/**
 * メディア情報ダイアログ (共通コンポーネント)
 *
 * 動画 / 音楽の情報表示・タイトル・メモ編集に使われる共通ダイアログ。
 * メディア種別ごとの追加フィールドは `renderExtraFields` で差し込む。
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { formatBytes } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** メディアレコードの共通インターフェース */
export type BaseMediaRecord = {
  id: string
  title: string
  memo?: string
  originalUrl: string
  savedAt: number
  fileSize: number
}

export type MediaInfoDialogProps<T extends BaseMediaRecord> = {
  item: T | null
  dialogTitle: string
  /** Dexie update 関数 */
  onUpdate: (id: string, changes: { title: string; memo: string }) => Promise<void>
  onClose: () => void
  onSaved: () => void
  /** メディア種別固有のフィールドを描画する */
  renderExtraFields?: (item: T) => ReactNode
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MediaInfoDialog = <T extends BaseMediaRecord>({
  item,
  dialogTitle,
  onUpdate,
  onClose,
  onSaved,
  renderExtraFields,
}: MediaInfoDialogProps<T>) => {
  const [editedTitle, setEditedTitle] = useState('')
  const [editedMemo, setEditedMemo] = useState('')

  useEffect(() => {
    if (item) {
      setEditedTitle(item.title)
      setEditedMemo(item.memo || '')
    }
  }, [item])

  const handleSave = useCallback(async () => {
    if (!item) return
    try {
      await onUpdate(item.id, { title: editedTitle, memo: editedMemo })
      toast.success('情報を更新しました')
      onSaved()
      onClose()
    } catch (error) {
      console.error('Failed to update info:', error)
      toast.error('情報の更新に失敗しました')
    }
  }, [item, editedTitle, editedMemo, onUpdate, onSaved, onClose])

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>タイトルを編集したり、詳細情報を確認できます</DialogDescription>
        </DialogHeader>

        {item && (
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
            {/* タイトル編集 */}
            <div className="space-y-2">
              <Label htmlFor="title">タイトル</Label>
              <Input id="title" value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} />
            </div>

            {/* メモ編集 */}
            <div className="space-y-2">
              <Label htmlFor="memo">メモ</Label>
              <Textarea
                id="memo"
                value={editedMemo}
                onChange={(e) => setEditedMemo(e.target.value)}
                placeholder="メモを入力..."
                rows={3}
              />
            </div>

            <Separator />

            {/* 情報表示 */}
            <div className="space-y-3">
              {/* メディア固有フィールド */}
              {renderExtraFields?.(item)}

              <div>
                <Label className="text-xs text-muted-foreground">オリジナルURL</Label>
                <p className="text-sm break-all">{item.originalUrl}</p>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">保存日時</Label>
                <p className="text-sm">{new Date(item.savedAt).toLocaleString('ja-JP')}</p>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">ファイルサイズ</Label>
                <p className="text-sm">{formatBytes(item.fileSize)}</p>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">ID</Label>
                <p className="text-sm font-mono">{item.id}</p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={onClose} className="flex-1">
              キャンセル
            </Button>
            <Button onClick={handleSave} className="flex-1">
              保存
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
